import React, { useState, useRef, useEffect } from 'react';
import { styled } from 'baseui';
import { Select } from 'baseui/select';
import { Button, KIND, SIZE } from 'baseui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/** Month written out (SAM-739): «Jul 14», with the year only when it is not this year. */
export const formatPickerDate = (date: Date | null | undefined, now: Date = new Date()): string => {
  if (!date) return '';
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (date.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return date.toLocaleDateString('en-US', opts);
};

const CalendarWrapper = styled('div', {
  position: 'relative',
  width: '100%',
});

const Input = styled('input', {
  width: '100%',
  height: '36px',
  padding: '8px 12px',
  fontSize: '14px',
  border: '1px solid #D1D5DB',
  borderRadius: '4px',
  outline: 'none',
  backgroundColor: '#fff',
  cursor: 'pointer',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  ':hover': {
    borderColor: '#9CA3AF',
  },
  ':focus': {
    borderColor: '#000',
    boxShadow: '0 0 0 1px #000',
  },
});

const PopoverContainer = styled<'div', { $withPortal: boolean }>('div', ({ $withPortal }) => ({
  position: $withPortal ? 'fixed' : 'absolute',
  top: $withPortal ? '50%' : 'calc(100% + 8px)',
  left: $withPortal ? '50%' : '0',
  transform: $withPortal ? 'translate(-50%, -50%)' : 'none',
  zIndex: 99999,
  backgroundColor: '#fff',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  padding: '24px',
  minWidth: '380px',
}));

const Overlay = styled('div', {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 99998,
});

const CalendarHeader = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  gap: '16px',
});

const NavButton = styled('button', {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  transition: 'background-color 0.2s',
  color: '#000',
  ':hover': {
    backgroundColor: '#F3F4F6',
  },
});

const MonthYearSelector = styled('div', {
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
});

const SelectorText = styled('span', {
  fontSize: '18px',
  fontWeight: '400',
  color: '#000',
});

const DayNames = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '4px',
  marginBottom: '12px',
});

const DayName = styled('div', {
  color: '#6B7280',
  fontSize: '16px',
  fontWeight: '400',
  textAlign: 'center',
  padding: '8px 0',
});

const DaysGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '4px',
});

const Day = styled<'button', { 
  $isSelected?: boolean; 
  $isInRange?: boolean; 
  $isRangeStart?: boolean;
  $isRangeEnd?: boolean;
  $isDisabled?: boolean; 
  $isOutsideMonth?: boolean;
}>('button', ({ $isSelected, $isInRange, $isRangeStart, $isRangeEnd, $isDisabled, $isOutsideMonth }) => ({
  width: '48px',
  height: '48px',
  borderRadius: $isInRange && !$isRangeStart && !$isRangeEnd ? '0' : '50%',
  border: $isSelected || $isRangeStart || $isRangeEnd ? '2px solid #000' : 'none',
  backgroundColor: $isInRange && !$isRangeStart && !$isRangeEnd ? '#F3F4F6' : 'transparent',
  color: $isDisabled || $isOutsideMonth ? '#D1D5DB' : '#000',
  fontSize: '16px',
  fontWeight: '400',
  cursor: $isDisabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s',
  ':hover': {
    backgroundColor: $isDisabled ? 'transparent' : 
                     ($isInRange && !$isRangeStart && !$isRangeEnd) ? '#E5E7EB' : '#F3F4F6',
    borderRadius: $isInRange && !$isRangeStart && !$isRangeEnd ? '0' : '50%',
  },
  ':disabled': {
    cursor: 'not-allowed',
  },
}));

interface BaseDatePickerProps {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  rangeValue?: { from?: Date; to?: Date };
  onRangeChange?: (range: { from?: Date; to?: Date } | undefined) => void;
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  disabled?: boolean;
  withPortal?: boolean;
  mode?: 'single' | 'range';
  /** When true, renders the calendar inline (no input, always visible). Use inside Popovers. */
  inline?: boolean;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const BaseDatePicker: React.FC<BaseDatePickerProps> = ({
  value,
  onChange,
  rangeValue,
  onRangeChange,
  minDate,
  maxDate,
  placeholder = 'Select date',
  disabled = false,
  withPortal = false,
  mode = 'single',
  inline = false,
}) => {
  const [isOpen, setIsOpen] = useState(inline);
  const [currentMonth, setCurrentMonth] = useState(
    mode === 'single' && value ? value.getMonth() : 
    mode === 'range' && rangeValue?.from ? rangeValue.from.getMonth() :
    new Date().getMonth()
  );
  const [currentYear, setCurrentYear] = useState(
    mode === 'single' && value ? value.getFullYear() : 
    mode === 'range' && rangeValue?.from ? rangeValue.from.getFullYear() :
    new Date().getFullYear()
  );
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (inline) return; // Inline mode: parent Popover handles closing
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, inline]);

  const formatDate = (date: Date | null | undefined): string => formatPickerDate(date);

  const formatValue = (): string => {
    if (mode === 'range' && rangeValue) {
      if (rangeValue.from && rangeValue.to) {
        return `${formatDate(rangeValue.from)} – ${formatDate(rangeValue.to)}`;
      } else if (rangeValue.from) {
        return formatDate(rangeValue.from);
      }
    } else if (mode === 'single' && value) {
      return formatDate(value);
    }
    return '';
  };

  const getDaysInMonth = (month: number, year: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number): number => {
    return new Date(year, month, 1).getDay();
  };

  const isDateDisabled = (date: Date): boolean => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const isSameDay = (date1: Date | null | undefined, date2: Date): boolean => {
    if (!date1) return false;
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const isInRange = (date: Date): boolean => {
    if (mode !== 'range') return false;
    
    // Show confirmed range
    if (rangeValue?.from && rangeValue?.to) {
      return date >= rangeValue.from && date <= rangeValue.to;
    }
    
    // Show preview range when hovering (only start date selected)
    if (rangeValue?.from && !rangeValue?.to && hoverDate) {
      const start = rangeValue.from < hoverDate ? rangeValue.from : hoverDate;
      const end = rangeValue.from < hoverDate ? hoverDate : rangeValue.from;
      return date >= start && date <= end;
    }
    
    return false;
  };

  const handleDayClick = (day: number) => {
    const selectedDate = new Date(currentYear, currentMonth, day);
    if (isDateDisabled(selectedDate)) return;

    if (mode === 'single') {
      if (onChange) {
        onChange(selectedDate);
        setIsOpen(false);
      }
    } else if (mode === 'range' && onRangeChange) {
      if (!rangeValue?.from || (rangeValue.from && rangeValue.to)) {
        // Start new range
        onRangeChange({ from: selectedDate, to: undefined });
      } else {
        // Complete range
        if (selectedDate < rangeValue.from) {
          onRangeChange({ from: selectedDate, to: rangeValue.from });
        } else {
          onRangeChange({ from: rangeValue.from, to: selectedDate });
        }
        setIsOpen(false);
      }
    }
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const renderDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days: JSX.Element[] = [];

    // Previous month days
    const prevMonthDays = getDaysInMonth(currentMonth - 1, currentYear);
    for (let i = firstDay - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      days.push(
        <Day key={`prev-${day}`} $isOutsideMonth disabled>
          {day}
        </Day>
      );
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const isSelected = mode === 'single' && isSameDay(value, date);
      const isRangeStart = mode === 'range' && isSameDay(rangeValue?.from, date);
      const isRangeEnd = mode === 'range' && isSameDay(rangeValue?.to, date);
      const inRange = mode === 'range' && isInRange(date);
      const isDisabled = isDateDisabled(date);

      days.push(
        <Day
          key={`current-${day}`}
          onClick={() => handleDayClick(day)}
          onMouseEnter={() => {
            if (mode === 'range' && rangeValue?.from && !rangeValue?.to && !isDisabled) {
              setHoverDate(date);
            }
          }}
          onMouseLeave={() => {
            if (mode === 'range') {
              setHoverDate(null);
            }
          }}
          $isSelected={isSelected}
          $isRangeStart={isRangeStart}
          $isRangeEnd={isRangeEnd}
          $isInRange={inRange}
          $isDisabled={isDisabled}
          disabled={isDisabled}
        >
          {day}
        </Day>
      );
    }

    // Next month days to fill the grid
    const totalDays = days.length;
    const remainingDays = totalDays % 7 === 0 ? 0 : 7 - (totalDays % 7);
    for (let day = 1; day <= remainingDays; day++) {
      days.push(
        <Day key={`next-${day}`} $isOutsideMonth disabled>
          {day}
        </Day>
      );
    }

    return days;
  };

  const currentYearStart = new Date().getFullYear() - 10;
  const years = Array.from({ length: 21 }, (_, i) => currentYearStart + i);

  const calendarContent = (
    <>
      <CalendarHeader>
        <NavButton onClick={handlePrevMonth}>
          <ChevronLeft size={20} />
        </NavButton>

        <MonthYearSelector>
          <SelectorText>
            {MONTHS[currentMonth]} {currentYear}
          </SelectorText>
        </MonthYearSelector>

        <NavButton onClick={handleNextMonth}>
          <ChevronRight size={20} />
        </NavButton>
      </CalendarHeader>

      <DayNames>
        {DAY_NAMES.map((name) => (
          <DayName key={name}>{name}</DayName>
        ))}
      </DayNames>

      <DaysGrid>{renderDays()}</DaysGrid>
    </>
  );

  // Inline mode: render calendar directly (for use inside Popovers)
  if (inline) {
    return (
      <div ref={wrapperRef} style={{ minWidth: '340px' }}>
        {calendarContent}
      </div>
    );
  }

  return (
    <CalendarWrapper ref={wrapperRef}>
      <Input
        type="text"
        value={formatValue()}
        placeholder={placeholder}
        onClick={() => !disabled && setIsOpen(true)}
        readOnly
        disabled={disabled}
      />

      {isOpen && (
        <>
          {withPortal && <Overlay onClick={() => setIsOpen(false)} />}
          <PopoverContainer $withPortal={withPortal}>
            {calendarContent}
          </PopoverContainer>
        </>
      )}
    </CalendarWrapper>
  );
};
